import { getToolName, type DynamicToolUIPart, type ToolUIPart } from "ai";
import { Loader2 } from "lucide-react";

import { Card, CardContent } from "./card";
import { cn } from "./cn";

export type ToolCallCardPart = ToolUIPart | DynamicToolUIPart;

const PENDING_LABELS: Record<string, string> = {
  check_availability: "Checking availability…",
  create_booking: "Creating your booking…",
  get_booking: "Looking up booking…",
  reschedule_booking: "Rescheduling booking…",
};

function pendingLabelForTool(toolName: string): string {
  return PENDING_LABELS[toolName] ?? `${humanizeToolName(toolName)}…`;
}

function humanizeToolName(toolName: string): string {
  return toolName
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function shortLocation(location: string): string {
  const t = location.trim();
  if (!t) return "venue";
  const comma = t.indexOf(",");
  if (comma > 0) return t.slice(0, comma).trim();
  const first = t.split(/\s+/)[0];
  return first || t;
}

function formatWhen(dateStr: unknown, timeStr: unknown): string {
  if (typeof dateStr !== "string" || typeof timeStr !== "string") return "";
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const tm = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(timeStr);
  if (!dm || !tm) return "";
  const y = Number(dm[1]);
  const mo = Number(dm[2]);
  const d = Number(dm[3]);
  const hh = Number(tm[1]);
  const mm = Number(tm[2]);
  const dt = new Date(y, mo - 1, d, hh, mm);
  const weekday = dt.toLocaleDateString("en-IE", { weekday: "long" });
  const hour24 = dt.getHours();
  const h12 = hour24 % 12 || 12;
  const suffix = hour24 >= 12 ? "pm" : "am";
  const timePart = mm === 0 ? `${h12}${suffix}` : `${h12}:${String(mm).padStart(2, "0")}${suffix}`;
  return `${weekday} ${timePart}`;
}

function summarizeOutput(toolName: string, output: unknown, input: unknown): string {
  const inp = isRecord(input) ? input : {};

  switch (toolName) {
    case "check_availability": {
      const out = isRecord(output) ? output : {};
      const available = out.available === true;
      const msg = typeof out.message === "string" ? out.message : "";
      if (available) {
        const when = formatWhen(inp.date, inp.time);
        const loc = shortLocation(String(inp.location ?? ""));
        return when ? `✓ Slot available at ${loc}, ${when}` : `✓ Slot available at ${loc}`;
      }
      return msg.startsWith("✗") ? msg : `✗ ${msg || "Slot not available"}`;
    }
    case "create_booking": {
      const out = isRecord(output) ? output : {};
      const bookingId = typeof out.bookingId === "string" ? out.bookingId : "";
      const details = isRecord(out.confirmationDetails) ? out.confirmationDetails : inp;
      const loc = shortLocation(String(details.location ?? inp.location ?? ""));
      const when = formatWhen(details.date ?? inp.date, details.time ?? inp.time);
      const idPart = bookingId ? `${bookingId} — ` : "";
      return when
        ? `✓ Booking created — ${idPart}${loc}, ${when}`
        : `✓ Booking created${bookingId ? ` — ${bookingId}` : ""}`;
    }
    case "get_booking": {
      const out = isRecord(output) ? output : {};
      if (out.success === false && typeof out.error === "string") {
        return `✗ ${out.error}`;
      }
      const bookingId = typeof out.bookingId === "string" ? out.bookingId : String(inp.bookingId ?? "");
      const loc = shortLocation(String(out.location ?? ""));
      const when = formatWhen(out.date, out.time);
      if (when && loc) return `✓ Booking ${bookingId} — ${loc}, ${when}`;
      if (bookingId) return `✓ Booking found — ${bookingId}`;
      return "✓ Booking details loaded";
    }
    case "reschedule_booking": {
      const out = isRecord(output) ? output : {};
      if (out.success === false && typeof out.error === "string") {
        return `✗ ${out.error}`;
      }
      const loc = shortLocation(String(out.location ?? ""));
      const when = formatWhen(out.date, out.time);
      return when ? `✓ Rescheduled — ${loc}, ${when}` : "✓ Booking rescheduled";
    }
    default:
      return typeof output === "string" ? `✓ ${output}` : "✓ Done";
  }
}

function isPendingState(
  state: ToolCallCardPart["state"]
): state is "input-streaming" | "input-available" | "approval-requested" | "approval-responded" {
  return (
    state === "input-streaming" ||
    state === "input-available" ||
    state === "approval-requested" ||
    state === "approval-responded"
  );
}

export function ToolCallCard({ part }: { part: ToolCallCardPart }) {
  const toolName = getToolName(part);
  const pending = isPendingState(part.state);
  const label = pendingLabelForTool(toolName);

  let summary: string | null = null;
  if (part.state === "output-available") {
    summary = summarizeOutput(toolName, part.output, part.input);
  } else if (part.state === "output-error") {
    summary = part.errorText ? `✗ ${part.errorText}` : "✗ Something went wrong";
  } else if (part.state === "output-denied") {
    summary = "✗ Action was not approved";
  }

  return (
    <Card
      className={cn(
        "max-w-[85%] border-l-4 border-l-amber-500/90 shadow-sm",
        "border-neutral-200 bg-amber-50/40 dark:border-neutral-800 dark:bg-amber-950/20",
        !pending && "border-l-emerald-600/80 dark:border-l-emerald-500/70"
      )}
    >
      <CardContent className="flex items-start gap-3 px-4 py-3">
        {pending ? (
          <Loader2
            className="mt-0.5 size-4 shrink-0 animate-spin text-amber-700 dark:text-amber-400"
            aria-hidden
          />
        ) : null}
        <div className="min-w-0 flex-1">
          {pending ? (
            <p className="text-sm font-medium leading-snug text-neutral-800 dark:text-neutral-100">{label}</p>
          ) : (
            <p className="text-sm font-medium leading-relaxed text-neutral-800 dark:text-neutral-100">
              {summary ?? "✓ Done"}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
